<main class="artifacts" (keydown.escape)="onArtifactsEscape()">
  <header class="artifacts__header">
    <div class="artifacts__heading">
      <h1 class="artifacts__title">Artifacts</h1>
      <p class="artifacts__lead">Import models, compose them into scenes and follow background processing.</p>
    </div>
    <button
      type="button"
      class="artifacts-button artifacts-button--primary"
      [disabled]="uploadInProgress"
      (click)="openArtifactsImport()"
    >
      <span aria-hidden="true">+</span> Import model
    </button>
  </header>

  <div
    *ngIf="ingestState.error !== null || ingestState.message"
    class="artifacts-notice"
    [class.artifacts-notice--error]="ingestState.error !== null"
    [attr.role]="ingestState.error !== null ? 'alert' : 'status'"
  >
    <span class="artifacts-notice__text">{{ ingestState.error ?? ingestState.message }}</span>
    <button
      type="button"
      class="artifacts-icon-button"
      aria-label="Dismiss message"
      title="Dismiss"
      (click)="dismissArtifactsNotice()"
    >
      &#x2715;
    </button>
  </div>

  <div class="artifacts-tabs" role="tablist" aria-label="Artifact types" (keydown)="onArtifactsTabKeydown($event)">
    <button
      id="artifacts-tab-models"
      type="button"
      role="tab"
      class="artifacts-tabs__tab"
      aria-controls="artifacts-panel-models"
      [attr.aria-selected]="artifactsTab === 'models'"
      [attr.tabindex]="artifactsTab === 'models' ? 0 : -1"
      (click)="setArtifactsTab('models')"
    >
      Models <span class="artifacts-count">{{ modelsRegistry.length }}</span>
    </button>
    <button
      id="artifacts-tab-scenes"
      type="button"
      role="tab"
      class="artifacts-tabs__tab"
      aria-controls="artifacts-panel-scenes"
      [attr.aria-selected]="artifactsTab === 'scenes'"
      [attr.tabindex]="artifactsTab === 'scenes' ? 0 : -1"
      (click)="setArtifactsTab('scenes')"
    >
      Scenes <span class="artifacts-count">{{ scenesRegistry.length }}</span>
    </button>
    <button
      id="artifacts-tab-processings"
      type="button"
      role="tab"
      class="artifacts-tabs__tab"
      aria-controls="artifacts-panel-processings"
      [attr.aria-selected]="artifactsTab === 'processings'"
      [attr.tabindex]="artifactsTab === 'processings' ? 0 : -1"
      (click)="setArtifactsTab('processings')"
    >
      Processings
      <span
        class="artifacts-count"
        [class.artifacts-count--active]="activeProcessingsCount > 0"
        [attr.title]="activeProcessingsCount > 0 ? activeProcessingsCount + ' running' : null"
      >
        {{ activeProcessingsCount > 0 ? activeProcessingsCount + ' running' : processingsRegistry.length }}
      </span>
    </button>
  </div>

  <!-- Models -->
  <section
    *ngIf="artifactsTab === 'models'"
    id="artifacts-panel-models"
    class="artifacts-workspace"
    role="tabpanel"
    aria-labelledby="artifacts-tab-models"
  >
    <div class="artifacts-master">
      <div class="artifacts-master__toolbar">
        <input
          id="modelSearch"
          class="artifacts-search"
          type="search"
          placeholder="Search by name or id"
          aria-label="Search models"
          [value]="modelSearchQuery"
          (input)="onModelSearchChanged($event)"
        />
        <button
          type="button"
          class="artifacts-icon-button"
          aria-label="Refresh models"
          title="Refresh models"
          (click)="refreshModelsRegistry()"
        >
          &#x21bb;
        </button>
      </div>

      <div class="artifacts-bulk" [class.artifacts-bulk--active]="similarityBulkModelIds.length > 0">
        <label class="artifacts-bulk__select-all">
          <input
            type="checkbox"
            [checked]="areAllVisibleModelsSelectedForSimilarity"
            [indeterminate]="similarityBulkModelIds.length > 0 && !areAllVisibleModelsSelectedForSimilarity"
            [disabled]="similarityBulkPending || filteredModelsRegistry.length === 0"
            aria-label="Select all listed models for similarity rerun"
            (change)="toggleAllVisibleModelsForSimilarity($event)"
          />
          <span *ngIf="similarityBulkModelIds.length === 0">
            {{ filteredModelsRegistry.length }} of {{ modelsRegistry.length }} models
          </span>
          <span *ngIf="similarityBulkModelIds.length > 0">{{ similarityBulkModelIds.length }} selected</span>
        </label>
        <ng-container *ngIf="similarityBulkModelIds.length > 0">
          <button
            type="button"
            class="artifacts-link-button"
            [disabled]="similarityBulkPending"
            (click)="clearSimilarityBulkSelection()"
          >
            Clear
          </button>
        </ng-container>
      </div>
      <div *ngIf="similarityBulkModelIds.length > 0" class="artifacts-bulk-action" aria-label="Similarity rerun">
        <label class="artifacts-switch">
          <input
            type="checkbox"
            [checked]="similarityFullRerun"
            [disabled]="similarityBulkPending"
            (change)="onSimilarityFullRerunChanged($event)"
          />
          Full rerun: restore previous replacements first (no reparse needed)
        </label>
        <p class="artifacts-hint">
          Runs in the background. Meshes with incompatible textures, vertex colors, or scene material overrides keep
          their original geometry; vertex-dependent materials cannot be remapped reliably across different meshes.
        </p>
        <button
          type="button"
          class="artifacts-button artifacts-button--primary"
          [disabled]="uploadInProgress || similarityBulkPending"
          (click)="rerunSimilarityForSelectedModels()"
        >
          {{
            similarityBulkPending
              ? 'Queuing similarity...'
              : 'Rerun similarity for ' +
                similarityBulkModelIds.length +
                (similarityBulkModelIds.length === 1 ? ' model' : ' models')
          }}
        </button>
      </div>

      <div class="artifacts-list" role="list" aria-label="Models">
        <div *ngIf="modelsRegistry.length === 0" class="artifacts-empty">
          <strong>No models yet</strong>
          <span>Import an IFC, RVM or OBJ file, a photogrammetry folder or an RVM/ATT project.</span>
          <button type="button" class="artifacts-button" (click)="openArtifactsImport()">Import model</button>
        </div>
        <div *ngIf="modelsRegistry.length > 0 && filteredModelsRegistry.length === 0" class="artifacts-empty">
          <strong>No models match "{{ modelSearchQuery }}"</strong>
        </div>
        <div
          *ngFor="let model of filteredModelsRegistry"
          class="artifacts-row"
          role="listitem"
          [class.artifacts-row--selected]="model.id === selectedModelId"
        >
          <input
            type="checkbox"
            class="artifacts-row__check"
            [checked]="similarityBulkModelIds.includes(model.id)"
            [disabled]="similarityBulkPending"
            [attr.aria-label]="'Select ' + getModelTitle(model) + ' for similarity rerun'"
            (change)="onSimilarityBulkModelChanged(model.id, $event)"
          />
          <button
            type="button"
            class="artifacts-row__main"
            [attr.aria-current]="model.id === selectedModelId ? 'true' : null"
            (click)="selectModelFromDropdown(model.id); revealArtifactDetailOnNarrowScreens()"
          >
            <span class="artifacts-row__title">{{ getModelTitle(model) }}</span>
            <span class="artifacts-row__meta">
              <code>{{ model.id.slice(0, 6) }}</code>
              <time>{{ getModelDisplayDate(model) }}</time>
            </span>
          </button>
        </div>
      </div>

      <details *ngIf="similarityBulkResults.length > 0" class="artifacts-tasks" open>
        <summary>Similarity reruns ({{ similarityBulkResults.length }})</summary>
        <ul role="status">
          <li *ngFor="let result of similarityBulkResults" class="artifacts-task">
            <span class="artifacts-task__title">{{ getModelTitleById(result.modelId) }}</span>
            <span class="artifacts-status" [attr.data-status]="result.status">{{ result.status }}</span>
            <span class="artifacts-task__meta">
              {{ result.processingId ?? 'starting' }}
            </span>
            <span *ngIf="result.error" class="artifacts-task__error">{{ result.error }}</span>
          </li>
        </ul>
      </details>
    </div>

    <div class="artifacts-detail" aria-live="polite">
      <div *ngIf="selectedModel === undefined" class="artifacts-empty artifacts-empty--detail">
        <strong>Select a model</strong>
        <span>See which scenes use it, its similarity history and available actions.</span>
      </div>

      <ng-container *ngIf="selectedModel as model">
        <header class="artifacts-detail__header">
          <div class="artifacts-detail__identity">
            <span class="artifacts-eyebrow">Model</span>
            <h2 *ngIf="artifactRenameTarget !== 'model'" class="artifacts-detail__title">{{ getModelTitle(model) }}</h2>
            <form
              *ngIf="artifactRenameTarget === 'model'"
              class="artifacts-rename"
              (submit)="$event.preventDefault(); renameSelectedModel()"
            >
              <input
                type="text"
                aria-label="Model name"
                [value]="selectedModelRenameDraft"
                (input)="onSelectedModelRenameDraftChanged($event)"
              />
              <button type="submit" class="artifacts-button artifacts-button--primary">Save</button>
              <button type="button" class="artifacts-button" (click)="cancelArtifactRename()">Cancel</button>
            </form>
            <div class="artifacts-detail__meta">
              <button
                type="button"
                class="artifacts-id"
                [attr.aria-label]="'Copy model id ' + model.id"
                title="Copy id"
                (click)="copyArtifactId(model.id)"
              >
                <code>{{ model.id }}</code>
                <span>{{ artifactsCopiedId === model.id ? 'Copied' : 'Copy' }}</span>
              </button>
              <time>Created {{ getModelDisplayDate(model) }}</time>
            </div>
          </div>
          <div class="artifacts-detail__secondary">
            <button
              *ngIf="artifactRenameTarget !== 'model'"
              type="button"
              class="artifacts-button"
              (click)="beginModelRename(model.id)"
            >
              Rename
            </button>
            <button
              type="button"
              class="artifacts-button"
              title="Parse the source files again; affected scenes are updated"
              [disabled]="uploadInProgress || similarityBulkPending"
              (click)="reparseModelFromRegistry(model.id)"
            >
              Reparse
            </button>
            <button type="button" class="artifacts-button artifacts-button--danger" (click)="requestModelDelete(model)">
              Delete
            </button>
          </div>
        </header>

        <div class="artifacts-actions" role="group" aria-label="Use this model">
          <button type="button" class="artifacts-action-card" (click)="openArtifactsSceneAction('create', model.id)">
            <strong>Create scene</strong>
            <span>New scene that contains this model</span>
          </button>
          <button
            type="button"
            class="artifacts-action-card"
            [disabled]="scenesRegistry.length === 0"
            (click)="openArtifactsSceneAction('attach', model.id)"
          >
            <strong>Attach to scene</strong>
            <span>Add the model to an existing scene</span>
          </button>
          <button
            type="button"
            class="artifacts-action-card"
            [disabled]="scenesRegistry.length === 0"
            (click)="openArtifactsSceneAction('instance', model.id)"
          >
            <strong>Add as instance</strong>
            <span>Place transformed copies in a scene</span>
          </button>
        </div>

        <section class="artifacts-section" aria-label="Scenes containing this model">
          <div class="artifacts-section__header">
            <h3>Used in scenes</h3>
            <button
              type="button"
              class="artifacts-icon-button"
              aria-label="Refresh model scenes"
              title="Refresh"
              (click)="refreshSelectedModelScenes()"
            >
              &#x21bb;
            </button>
          </div>
          <div *ngIf="selectedModelScenesLoading" class="artifacts-hint" role="status">Loading model scenes...</div>
          <div *ngIf="selectedModelScenesError !== null" class="artifacts-error" role="alert">
            Could not load model scenes: {{ selectedModelScenesError }}
          </div>
          <div
            *ngIf="!selectedModelScenesLoading && selectedModelScenesError === null && selectedModelScenes.length === 0"
            class="artifacts-hint"
          >
            This model is not in any scenes yet.
          </div>
          <ul
            *ngIf="!selectedModelScenesLoading && selectedModelScenesError === null && selectedModelScenes.length > 0"
            class="artifacts-links"
          >
            <li *ngFor="let participation of selectedModelScenes" class="artifacts-links__item">
              <button type="button" class="artifacts-links__main" (click)="showSceneInArtifacts(participation.sceneId)">
                <span class="artifacts-row__title">{{ getSceneTitleById(participation.sceneId) }}</span>
                <span class="artifacts-row__meta">
                  {{ participation.originalRootNodeIds.length }}
                  {{ participation.originalRootNodeIds.length === 1 ? 'original root' : 'original roots' }},
                  {{ participation.instanceCount }} {{ participation.instanceCount === 1 ? 'instance' : 'instances' }}
                </span>
              </button>
              <a class="artifacts-button" [routerLink]="['/viewer']" [queryParams]="{ sceneId: participation.sceneId }">
                Open in viewer
              </a>
            </li>
          </ul>
        </section>

        <section class="artifacts-section" aria-label="Similarity results history">
          <div class="artifacts-section__header">
            <h3>Similarity history</h3>
            <button
              type="button"
              class="artifacts-icon-button"
              aria-label="Refresh similarity history"
              title="Refresh"
              (click)="refreshSelectedModelSimilarityRuns()"
            >
              &#x21bb;
            </button>
          </div>
          <div *ngIf="selectedModelSimilarityRunsLoading" class="artifacts-hint" role="status">
            Loading similarity results...
          </div>
          <div *ngIf="selectedModelSimilarityRunsError !== null" class="artifacts-error" role="alert">
            Could not load similarity results: {{ selectedModelSimilarityRunsError }}
          </div>
          <div
            *ngIf="!selectedModelSimilarityRunsLoading && selectedModelSimilarityRunsError === null && selectedModelSimilarityRuns.length === 0"
            class="artifacts-hint"
          >
            No similarity runs for this model. Select it in the list to rerun similarity.
          </div>
          <div
            *ngIf="!selectedModelSimilarityRunsLoading && selectedModelSimilarityRunsError === null && selectedModelSimilarityRuns.length > 0"
            class="artifacts-table-scroll"
            tabindex="0"
            role="region"
            aria-label="Similarity runs"
          >
            <table class="artifacts-table">
              <thead>
                <tr>
                  <th scope="col">Started</th>
                  <th scope="col">Status</th>
                  <th scope="col" class="artifacts-table__number">Total</th>
                  <th scope="col" class="artifacts-table__number">Unique</th>
                  <th scope="col" class="artifacts-table__number">Matched</th>
                  <th scope="col" class="artifacts-table__number">Skipped</th>
                  <th scope="col">Completed</th>
                  <th scope="col">Processing ID</th>
                </tr>
              </thead>
              <tbody>
                <ng-container *ngFor="let run of selectedModelSimilarityRuns">
                  <tr>
                    <td class="artifacts-table__nowrap">{{ run.startedAt | date: 'MMM d, y, HH:mm' }}</td>
                    <td>
                      <span class="artifacts-status" [attr.data-status]="run.status">{{ run.status }}</span>
                    </td>
                    <td class="artifacts-table__number">{{ run.totalObjectsCount }}</td>
                    <td class="artifacts-table__number">{{ run.uniqueObjectsCount ?? '—' }}</td>
                    <td class="artifacts-table__number">{{ run.matchedObjectsCount ?? '—' }}</td>
                    <td class="artifacts-table__number">{{ run.skippedObjectsCount ?? '—' }}</td>
                    <td class="artifacts-table__nowrap">
                      {{ run.completedAt ? (run.completedAt | date: 'MMM d, y, HH:mm') : '—' }}
                    </td>
                    <td>
                      <code>{{ run.processingId }}</code>
                    </td>
                  </tr>
                  <tr *ngIf="run.errorInfo" class="artifacts-table__error-row">
                    <td colspan="8">{{ run.errorInfo }}</td>
                  </tr>
                </ng-container>
              </tbody>
            </table>
          </div>
        </section>
      </ng-container>
    </div>
  </section>

  <!-- Scenes -->
  <section
    *ngIf="artifactsTab === 'scenes'"
    id="artifacts-panel-scenes"
    class="artifacts-workspace"
    role="tabpanel"
    aria-labelledby="artifacts-tab-scenes"
  >
    <div class="artifacts-master">
      <div class="artifacts-master__toolbar">
        <input
          id="sceneSearch"
          class="artifacts-search"
          type="search"
          placeholder="Search scenes"
          aria-label="Search scenes"
          [value]="sceneSearchQuery"
          (input)="onSceneSearchChanged($event)"
        />
        <button
          type="button"
          class="artifacts-icon-button"
          aria-label="Refresh scenes"
          title="Refresh scenes"
          (click)="refreshScenesRegistry()"
        >
          &#x21bb;
        </button>
      </div>
      <div class="artifacts-bulk">
        <span>{{ filteredScenesRegistry.length }} of {{ scenesRegistry.length }} scenes</span>
        <button
          type="button"
          class="artifacts-link-button"
          [disabled]="modelsRegistry.length === 0"
          (click)="openArtifactsSceneAction('create')"
        >
          + New scene
        </button>
      </div>
      <div class="artifacts-list" role="list" aria-label="Scenes">
        <div *ngIf="scenesRegistry.length === 0" class="artifacts-empty">
          <strong>No scenes yet</strong>
          <span>Create a scene from any imported model.</span>
          <button
            type="button"
            class="artifacts-button"
            [disabled]="modelsRegistry.length === 0"
            (click)="openArtifactsSceneAction('create')"
          >
            Create scene
          </button>
        </div>
        <div *ngIf="scenesRegistry.length > 0 && filteredScenesRegistry.length === 0" class="artifacts-empty">
          <strong>No scenes match "{{ sceneSearchQuery }}"</strong>
        </div>
        <div
          *ngFor="let scene of filteredScenesRegistry"
          class="artifacts-row"
          role="listitem"
          [class.artifacts-row--selected]="scene.id === selectedSceneId"
        >
          <button
            type="button"
            class="artifacts-row__main"
            [attr.aria-current]="scene.id === selectedSceneId ? 'true' : null"
            (click)="selectSceneFromDropdown(scene.id); revealArtifactDetailOnNarrowScreens()"
            (dblclick)="openSceneInViewer(scene.id)"
          >
            <span class="artifacts-row__title">{{ getSceneDisplayName(scene) }}</span>
            <span class="artifacts-row__meta">
              <code>{{ scene.id.slice(0, 6) }}</code>
              <time>{{ getSceneDisplayDate(scene) }}</time>
            </span>
          </button>
        </div>
      </div>
    </div>

    <div class="artifacts-detail" aria-live="polite">
      <div *ngIf="selectedScene === undefined" class="artifacts-empty artifacts-empty--detail">
        <strong>Select a scene</strong>
        <span>Open it in the viewer, manage its models and instances, or reuse geometry.</span>
      </div>

      <ng-container *ngIf="selectedScene as scene">
        <header class="artifacts-detail__header">
          <div class="artifacts-detail__identity">
            <span class="artifacts-eyebrow">Scene</span>
            <h2 *ngIf="artifactRenameTarget !== 'scene'" class="artifacts-detail__title">
              {{ getSceneDisplayName(scene) }}
            </h2>
            <form
              *ngIf="artifactRenameTarget === 'scene'"
              class="artifacts-rename"
              (submit)="$event.preventDefault(); renameSelectedScene()"
            >
              <input
                type="text"
                aria-label="Scene name"
                [value]="selectedSceneRenameDraft"
                (input)="onSelectedSceneRenameDraftChanged($event)"
              />
              <button type="submit" class="artifacts-button artifacts-button--primary">Save</button>
              <button type="button" class="artifacts-button" (click)="cancelArtifactRename()">Cancel</button>
            </form>
            <div class="artifacts-detail__meta">
              <button
                type="button"
                class="artifacts-id"
                [attr.aria-label]="'Copy scene id ' + scene.id"
                title="Copy id"
                (click)="copyArtifactId(scene.id)"
              >
                <code>{{ scene.id }}</code>
                <span>{{ artifactsCopiedId === scene.id ? 'Copied' : 'Copy' }}</span>
              </button>
              <time>Created {{ getSceneDisplayDate(scene) }}</time>
            </div>
          </div>
          <div class="artifacts-detail__secondary">
            <a
              class="artifacts-button artifacts-button--primary"
              [routerLink]="['/viewer']"
              [queryParams]="{ sceneId: scene.id }"
            >
              Open in viewer
            </a>
            <button
              *ngIf="artifactRenameTarget !== 'scene'"
              type="button"
              class="artifacts-button"
              (click)="beginSceneRename(scene.id)"
            >
              Rename
            </button>
            <button type="button" class="artifacts-button artifacts-button--danger" (click)="requestSceneDelete(scene)">
              Delete
            </button>
          </div>
        </header>

        <section class="artifacts-section" aria-label="Scene start view">
          <div class="artifacts-section__header">
            <h3>Start view</h3>
          </div>
          <label class="artifacts-field">
            <span class="artifacts-field__label">Quality profile</span>
            <select
              [value]="selectedSceneQualityProfile"
              [disabled]="sceneSettingsBusy"
              (change)="onSceneQualityProfileChanged($event)"
            >
              <option value="">Viewer default</option>
              <option *ngFor="let profile of visualQualityProfiles" [value]="profile.value">
                {{ profile.label }}
              </option>
            </select>
            <span class="artifacts-field__help"
              >Applied when the scene opens; a qualityProfile in the viewer URL takes precedence.</span
            >
          </label>
          <div class="artifacts-field">
            <span class="artifacts-field__label">Start camera</span>
            <div class="artifacts-section__actions">
              <span>{{ selectedSceneHasStartCamera ? 'Custom camera set' : 'Framed to the scene bounds' }}</span>
              <button
                *ngIf="selectedSceneHasStartCamera"
                type="button"
                class="artifacts-button"
                [disabled]="sceneSettingsBusy"
                (click)="clearSceneStartCamera()"
              >
                Clear
              </button>
            </div>
            <span class="artifacts-field__help"
              >Open the scene in the viewer, move the camera and choose "Use current camera as scene start". A camera in
              the viewer URL takes precedence.</span
            >
          </div>
          <div *ngIf="sceneSettingsStatus as status" role="status" class="artifacts-hint">{{ status.text }}</div>
        </section>

        <section class="artifacts-section" aria-label="Scene models">
          <div class="artifacts-section__header">
            <h3>
              Models in this scene <span class="artifacts-count">{{ sceneTreeRoots.length }}</span>
            </h3>
            <div class="artifacts-section__actions">
              <button
                type="button"
                class="artifacts-button"
                [disabled]="sceneModelActionPending || modelsRegistry.length === 0"
                (click)="openArtifactsSceneAction('attach')"
              >
                Attach models
              </button>
              <button
                type="button"
                class="artifacts-button"
                [disabled]="sceneModelActionPending || modelsRegistry.length === 0"
                (click)="openArtifactsSceneAction('instance')"
              >
                Add instances
              </button>
            </div>
          </div>
          <div *ngIf="sceneTreeRoots.length === 0" class="artifacts-hint">
            This scene has no models. Attach a model or add an instance.
          </div>
          <ul *ngIf="sceneTreeRoots.length > 0" class="artifacts-links" aria-label="Scene model roots">
            <li
              *ngFor="let rootNode of sceneTreeRoots"
              class="artifacts-links__item artifacts-links__item--stacked"
              [class.artifacts-links__item--selected]="rootNode.id === selectedSceneNodeId && sceneContextAction !== null"
            >
              <button
                type="button"
                class="artifacts-links__main"
                [attr.aria-expanded]="rootNode.id === selectedSceneNodeId && sceneContextAction !== null"
                [disabled]="sceneModelActionPending"
                (click)="selectSceneRootNode(rootNode.id)"
              >
                <span class="artifacts-row__title">{{ rootNode.title }}</span>
                <span class="artifacts-row__meta">
                  {{ rootNode.id === selectedSceneNodeId && sceneContextAction !== null ? 'Hide details' : 'Details' }}
                </span>
              </button>

              <div
                *ngIf="rootNode.id === selectedSceneNodeId && sceneContextAction !== null"
                class="artifacts-instance"
              >
                <div *ngIf="selectedSceneModelInstanceLoading" class="artifacts-hint" role="status">
                  Loading model instance offset...
                </div>
                <div *ngIf="selectedSceneModelInstanceError !== null" class="artifacts-error" role="alert">
                  Could not load model instance: {{ selectedSceneModelInstanceError }}
                </div>
                <ng-container *ngIf="!selectedSceneModelInstanceLoading && selectedSceneModelInstanceError === null">
                  <ng-container *ngIf="selectedSceneModelInstance as instance; else originalSceneRoot">
                    <p class="artifacts-hint">
                      Instance of model
                      <button
                        *ngIf="isModelInRegistry(instance.sourceModelId); else rawSourceModel"
                        type="button"
                        class="artifacts-link-button"
                        (click)="showModelInArtifacts(instance.sourceModelId)"
                      >
                        {{ getModelTitleById(instance.sourceModelId) }}
                      </button>
                      <ng-template #rawSourceModel
                        ><code>{{ instance.sourceModelId }}</code></ng-template
                      >
                    </p>
                    <dl *ngIf="sceneContextAction !== 'transform'" class="artifacts-transform-summary">
                      <dt>Translation</dt>
                      <dd>
                        {{ instance.relativeTransform[0] }}, {{ instance.relativeTransform[1] }},
                        {{ instance.relativeTransform[2] }}
                      </dd>
                      <dt>Rotation (°)</dt>
                      <dd>
                        {{ instance.relativeTransform[3] }}, {{ instance.relativeTransform[4] }},
                        {{ instance.relativeTransform[5] }}
                      </dd>
                      <dt>Scale</dt>
                      <dd>
                        {{ instance.relativeTransform[6] }}, {{ instance.relativeTransform[7] }},
                        {{ instance.relativeTransform[8] }}
                      </dd>
                    </dl>
                  </ng-container>
                  <ng-template #originalSceneRoot>
                    <p class="artifacts-hint">Original model root; it has no instance offset.</p>
                  </ng-template>
                </ng-container>

                <form
                  *ngIf="sceneContextAction === 'transform'"
                  class="artifacts-transform"
                  (submit)="$event.preventDefault(); transformSelectedSceneModel()"
                >
                  <span class="artifacts-field__label">Set absolute instance offset</span>
                  <fieldset
                    *ngFor="
                      let group of [
                        { key: 'translation', label: 'Translation' },
                        { key: 'rotation', label: 'Rotation (degrees)' },
                        { key: 'scale', label: 'Scale' }
                      ]
                    "
                    class="artifacts-axes"
                  >
                    <legend>{{ group.label }}</legend>
                    <label *ngFor="let axis of ['x', 'y', 'z']" class="artifacts-axes__axis">
                      <span>{{ axis | uppercase }}</span>
                      <input
                        type="number"
                        step="any"
                        [value]="$any(sceneTransformDraft)[group.key][axis]"
                        [disabled]="sceneModelActionPending"
                        [attr.aria-label]="group.label + ' ' + (axis | uppercase)"
                        (input)="onSceneTransformChanged($any(group.key), $any(axis), $event)"
                      />
                    </label>
                  </fieldset>
                  <div class="artifacts-form-actions">
                    <button
                      type="submit"
                      class="artifacts-button artifacts-button--primary"
                      [disabled]="
                        sceneModelActionPending || selectedSceneModelInstance === null || selectedSceneModelInstanceLoading
                      "
                    >
                      Apply transform
                    </button>
                    <button
                      type="button"
                      class="artifacts-button"
                      [disabled]="sceneModelActionPending"
                      (click)="sceneContextAction = 'instances'"
                    >
                      Cancel
                    </button>
                  </div>
                </form>

                <div *ngIf="sceneContextAction !== 'transform'" class="artifacts-form-actions">
                  <button
                    *ngIf="selectedSceneModelInstance !== null"
                    type="button"
                    class="artifacts-button"
                    [disabled]="selectedSceneModelInstanceLoading || sceneModelActionPending"
                    (click)="openSceneModelTransformAction()"
                  >
                    Transform instance
                  </button>
                  <button
                    type="button"
                    class="artifacts-button artifacts-button--danger"
                    [disabled]="selectedSceneModelInstanceLoading || sceneModelActionPending"
                    (click)="requestSceneRootRemoval()"
                  >
                    {{ selectedSceneModelInstance === null ? 'Remove model from scene' : 'Remove instance from scene' }}
                  </button>
                </div>
              </div>
            </li>
          </ul>
        </section>

        <section class="artifacts-section" aria-label="Scene geometry reuse">
          <div class="artifacts-section__header">
            <h3>Incremental geometry reuse</h3>
          </div>
          <p class="artifacts-hint">
            Reuses canonical meshes across the scene's models without restoring existing replacements. Photogrammetry is
            excluded.
          </p>
          <div class="artifacts-inline-form">
            <button
              type="button"
              class="artifacts-button"
              [disabled]="sceneReusePending || uploadInProgress"
              (click)="rerunSelectedSceneGeometryReuse()"
            >
              {{ sceneReusePending ? 'Queuing scene reuse...' : 'Reuse geometry' }}
            </button>
          </div>
          <ul *ngIf="sceneReuseResults.length > 0" class="artifacts-tasks__list" role="status">
            <li *ngFor="let result of sceneReuseResults" class="artifacts-task">
              <span class="artifacts-task__title">{{ getModelTitleById(result.modelId) }}</span>
              <span class="artifacts-status" [attr.data-status]="result.status">{{ result.status }}</span>
              <span class="artifacts-task__meta">
                {{ result.processingId }}
              </span>
              <span *ngIf="result.error" class="artifacts-task__error">{{ result.error }}</span>
            </li>
          </ul>
        </section>
      </ng-container>
    </div>
  </section>

  <!-- Processings -->
  <section
    *ngIf="artifactsTab === 'processings'"
    id="artifacts-panel-processings"
    class="artifacts-processings"
    role="tabpanel"
    aria-labelledby="artifacts-tab-processings"
  >
    <div class="artifacts-processings__toolbar">
      <div class="artifacts-filters" role="group" aria-label="Filter processings by status">
        <button
          *ngFor="
            let filter of [
              { key: 'all', label: 'All' },
              { key: 'active', label: 'Running' },
              { key: 'failed', label: 'Failed' },
              { key: 'completed', label: 'Completed' }
            ]
          "
          type="button"
          class="artifacts-filters__chip"
          [attr.aria-pressed]="artifactsProcessingsFilter === filter.key"
          (click)="setArtifactsProcessingsFilter($any(filter.key))"
        >
          {{ filter.label }} <span class="artifacts-count">{{ countProcessingsByFilter($any(filter.key)) }}</span>
        </button>
      </div>
      <div class="artifacts-processings__controls">
        <span class="artifacts-hint">Updated {{ processingsLastRefreshAt }}</span>
        <label class="artifacts-switch">
          <input
            type="checkbox"
            [checked]="processingsAutoRefreshEnabled"
            (change)="onProcessingsAutoRefreshChanged($event)"
          />
          Auto-refresh
        </label>
        <button type="button" class="artifacts-button" (click)="refreshProcessingsRegistry()">&#x21bb; Refresh</button>
        <button
          type="button"
          class="artifacts-button"
          [disabled]="getClearableProcessingsCount() === 0"
          (click)="requestProcessingsClear()"
        >
          Clear finished ({{ getClearableProcessingsCount() }})
        </button>
      </div>
    </div>

    <div class="artifacts-table-scroll" tabindex="0" role="region" aria-label="Processings">
      <table class="artifacts-table artifacts-table--processings">
        <thead>
          <tr>
            <th scope="col" aria-sort="descending">Started</th>
            <th scope="col">Files</th>
            <th scope="col">Status</th>
            <th scope="col">Duration</th>
            <th scope="col">Type</th>
            <th scope="col">Processing ID</th>
          </tr>
        </thead>
        <tbody>
          <ng-container *ngFor="let processing of displayedArtifactsProcessings">
            <tr
              class="artifacts-table__row"
              tabindex="0"
              [class.artifacts-table__row--selected]="selectedProcessingId === processing.id"
              [attr.aria-expanded]="selectedProcessingId === processing.id"
              [attr.aria-label]="'Processing ' + processing.id + ', ' + processing.status"
              (click)="toggleProcessingSelection(processing)"
              (keydown.enter)="toggleProcessingSelection(processing)"
              (keydown.space)="$event.preventDefault(); toggleProcessingSelection(processing)"
            >
              <td>
                {{
                  getProcessingStartedAt(processing) === null ? '—' : (getProcessingStartedAt(processing) | date: 'medium')
                }}
              </td>
              <td class="artifacts-table__files">
                <span [attr.title]="getProcessingFileName(processing)">{{ getProcessingFileName(processing) }}</span>
              </td>
              <td>
                <span class="artifacts-status" [attr.data-status]="processing.status">{{ processing.status }}</span>
              </td>
              <td class="artifacts-table__duration">{{ getProcessingDuration(processing) }}</td>
              <td>{{ getProcessingTypeLabel(processing) }}</td>
              <td>
                <code>{{ processing.id }}</code>
              </td>
            </tr>
            <tr
              *ngIf="selectedProcessingId === processing.id && selectedProcessingDetails !== null"
              class="artifacts-table__details"
            >
              <td colspan="6">
                <dl class="artifacts-processing-details">
                  <dt>Model</dt>
                  <dd>
                    <button
                      *ngIf="isModelInRegistry(selectedProcessingDetails.modelId); else rawProcessingModel"
                      type="button"
                      class="artifacts-link-button"
                      (click)="showModelInArtifacts(selectedProcessingDetails.modelId!)"
                    >
                      {{ getModelTitleById(selectedProcessingDetails.modelId!) }}
                    </button>
                    <ng-template #rawProcessingModel>{{ selectedProcessingDetails.modelId ?? '—' }}</ng-template>
                  </dd>
                  <ng-container *ngIf="getProcessingNodeProgress(selectedProcessingDetails) as nodeProgress">
                    <dt>Textures</dt>
                    <dd>{{ nodeProgress }}</dd>
                  </ng-container>
                  <dt>Metadata</dt>
                  <dd>{{ getProcessingMetadataSummary(selectedProcessingDetails) }}</dd>
                  <ng-container *ngIf="selectedProcessingDetails.errorInfo">
                    <dt>Error</dt>
                    <dd class="artifacts-error">{{ selectedProcessingDetails.errorInfo }}</dd>
                  </ng-container>
                </dl>
                <button type="button" class="artifacts-button" (click)="refreshSelectedProcessingStatus()">
                  Check status
                </button>
              </td>
            </tr>
          </ng-container>
          <tr *ngIf="displayedArtifactsProcessings.length === 0">
            <td colspan="6" class="artifacts-table__empty">
              {{ processingsRegistry.length === 0 ? 'No processings yet.' : 'No processings match this filter.' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="artifacts-processings__footer">
      <span class="artifacts-hint">
        Showing {{ displayedArtifactsProcessings.length }} of {{ filteredArtifactsProcessings.length }}
      </span>
      <button
        *ngIf="displayedArtifactsProcessings.length < filteredArtifactsProcessings.length"
        type="button"
        class="artifacts-button"
        (click)="showMoreArtifactsProcessings()"
      >
        Show more
      </button>
    </div>
  </section>

  <!-- Import dialog -->
  <div *ngIf="artifactsImportOpen" class="artifacts-dialog-backdrop" (click)="closeArtifactsImport()">
    <section
      class="artifacts-dialog artifacts-dialog--wide"
      role="dialog"
      aria-modal="true"
      aria-labelledby="artifactsImportTitle"
      (click)="$event.stopPropagation()"
    >
      <header class="artifacts-dialog__header">
        <h2 id="artifactsImportTitle">Import model</h2>
        <button type="button" class="artifacts-icon-button" aria-label="Close" (click)="closeArtifactsImport()">
          &#x2715;
        </button>
      </header>

      <div class="artifacts-segmented" role="radiogroup" aria-label="Import source">
        <label class="artifacts-segmented__option">
          <input
            type="radio"
            name="artifactsImportSource"
            value="files"
            [checked]="artifactsImportSource === 'files'"
            [disabled]="uploadInProgress"
            (change)="artifactsImportSource = 'files'"
          />
          <strong>Model files</strong>
          <span>IFC, RVM, OBJ and other parsers</span>
        </label>
        <label class="artifacts-segmented__option">
          <input
            type="radio"
            name="artifactsImportSource"
            value="photogrammetry"
            [checked]="artifactsImportSource === 'photogrammetry'"
            [disabled]="uploadInProgress"
            (change)="artifactsImportSource = 'photogrammetry'"
          />
          <strong>Photogrammetry folder</strong>
          <span>OBJ + MTL + textures as one model</span>
        </label>
        <label class="artifacts-segmented__option">
          <input
            type="radio"
            name="artifactsImportSource"
            value="rvm-att"
            [checked]="artifactsImportSource === 'rvm-att'"
            [disabled]="uploadInProgress || rvmAttValidationInProgress"
            (change)="artifactsImportSource = 'rvm-att'"
          />
          <strong>RVM/ATT folder</strong>
          <span>One model per RVM, optional ATT</span>
        </label>
      </div>

      <!-- Model files -->
      <form
        *ngIf="artifactsImportSource === 'files'"
        class="artifacts-form"
        (submit)="$event.preventDefault(); runIngestFlow()"
      >
        <label class="artifacts-field">
          <span class="artifacts-field__label">Files</span>
          <input
            id="modelUploadFile"
            type="file"
            multiple
            [disabled]="uploadInProgress"
            (change)="onUploadFileChanged($event)"
          />
          <span class="artifacts-field__help"
            >Each file becomes a separate model. Several files upload in parallel.</span
          >
        </label>
        <label class="artifacts-field">
          <span class="artifacts-field__label">Parser</span>
          <input
            id="modelUploadParser"
            type="text"
            list="artifactsParserTypes"
            [value]="ingestParserType"
            [disabled]="uploadInProgress"
            (input)="onUploadParserChanged($event)"
          />
          <datalist id="artifactsParserTypes">
            <option value="ifc"></option>
            <option value="rvm"></option>
            <option value="obj"></option>
          </datalist>
          <span class="artifacts-field__help">Detected from the file extension; change it if needed.</span>
        </label>
        <div class="artifacts-form-actions">
          <button type="submit" class="artifacts-button artifacts-button--primary" [disabled]="!canUploadModel">
            {{
              uploadInProgress
                ? 'Processing...'
                : selectedUploadFiles.length > 1
                ? 'Upload ' + selectedUploadFiles.length + ' models'
                : 'Upload model'
            }}
          </button>
        </div>
      </form>

      <!-- Photogrammetry -->
      <form
        *ngIf="artifactsImportSource === 'photogrammetry'"
        class="artifacts-form"
        (submit)="$event.preventDefault(); uploadPhotogrammetryProject()"
      >
        <p class="artifacts-hint">
          Select a folder with one or more OBJ files, their MTL libraries and PNG/JPEG textures. All OBJ files become
          one model. Relative paths and letter case must match the references. Export triangulated faces with positive
          indices and UV coordinates for textured faces. Only diffuse map_Kd textures without options are supported.
          Folder contents are not checked before upload; parsing errors appear in Processings.
        </p>
        <label class="artifacts-field">
          <span class="artifacts-field__label">Project folder</span>
          <input
            id="photogrammetryFolder"
            type="file"
            webkitdirectory
            multiple
            [disabled]="uploadInProgress"
            (change)="onPhotogrammetryFolderChanged($event)"
          />
          <span *ngIf="photogrammetryProject as project" class="artifacts-field__help">
            {{ project.objCount }} OBJ · {{ project.mtlCount }} MTL · {{ project.textureCount }} textures ·
            {{ project.totalBytes / 1048576 | number: '1.1-1' }} MiB
          </span>
        </label>
        <label class="artifacts-field">
          <span class="artifacts-field__label">Model name</span>
          <input
            id="photogrammetryModelName"
            type="text"
            [value]="photogrammetryModelName"
            [disabled]="uploadInProgress"
            (input)="onPhotogrammetryNameChanged($event)"
          />
        </label>
        <div class="artifacts-field">
          <span class="artifacts-field__label">Levels of detail</span>
          <span class="artifacts-field__help">
            Cuts all OBJ tiles into leaves of original geometry with their own texture atlases, then builds an HLOD
            tileset: a quadtree whose nodes merge and simplify their children with a bounded error. The scene imports
            only the root; the viewer streams the nodes the view needs by screen-space error, so the first image is fast
            and memory and network use follow the screen, not the model size.
          </span>
        </div>

        <details class="artifacts-advanced">
          <summary>Advanced settings</summary>
          <label class="artifacts-field">
            <span class="artifacts-field__label">Import matrix (16 row-major numbers)</span>
            <textarea
              id="photogrammetryImportMatrix"
              rows="2"
              [value]="photogrammetryImportMatrix"
              [disabled]="uploadInProgress"
              (input)="onPhotogrammetryImportMatrixChanged($event)"
            ></textarea>
          </label>
        </details>

        <div *ngIf="photogrammetryMessage" class="artifacts-hint" role="status" aria-live="polite">
          {{ photogrammetryMessage }}
        </div>
        <div *ngIf="photogrammetryError !== null" class="artifacts-error" role="alert">{{ photogrammetryError }}</div>
        <div class="artifacts-form-actions">
          <button
            type="submit"
            class="artifacts-button artifacts-button--primary"
            [disabled]="!canUploadPhotogrammetry"
          >
            {{ uploadInProgress ? 'Processing...' : 'Upload project as one model' }}
          </button>
        </div>
      </form>

      <!-- RVM/ATT -->
      <form
        *ngIf="artifactsImportSource === 'rvm-att'"
        class="artifacts-form"
        (submit)="$event.preventDefault(); uploadRvmAttProject()"
      >
        <p class="artifacts-hint">
          Each RVM becomes a separate model. ATT is optional; without it, the model has no ATT attributes. Matching ATT
          files must have the same name without the extension in the same subfolder (letter case is ignored). Binary RVM
          and UTF-8 ATT with NEW records and attribute := value lines are supported. Preflight checks pairing, the RVM
          header and ATT syntax; complete geometry is checked by the server.
        </p>
        <label class="artifacts-field">
          <span class="artifacts-field__label">RVM/ATT project folder</span>
          <input
            id="rvmAttFolder"
            type="file"
            webkitdirectory
            multiple
            [disabled]="uploadInProgress || rvmAttValidationInProgress"
            (change)="onRvmAttFolderChanged($event)"
          />
        </label>
        <div *ngIf="rvmAttMessage" class="artifacts-hint" role="status" aria-live="polite">{{ rvmAttMessage }}</div>
        <div *ngIf="rvmAttError !== null" class="artifacts-error" role="alert">{{ rvmAttError }}</div>
        <ng-container *ngIf="rvmAttProject as project">
          <div class="artifacts-hint">
            {{ project.pairs.length }} {{ project.pairs.length === 1 ? 'model' : 'models' }} ·
            {{ project.totalBytes / 1048576 | number: '1.1-1' }} MiB
          </div>
          <div *ngFor="let error of project.errors" class="artifacts-error" role="alert">{{ error }}</div>
          <div *ngFor="let warning of project.warnings" class="artifacts-warning">{{ warning }}</div>
          <ul class="artifacts-pairs">
            <li *ngFor="let pair of project.pairs" class="artifacts-pairs__item">
              <span class="artifacts-row__title">{{ pair.name }}</span>
              <span class="artifacts-status" [attr.data-status]="pair.processingId !== null ? 'Queued' : 'Pending'">
                {{ pair.processingId !== null ? 'Queued' : 'Not queued' }}
              </span>
              <span *ngIf="pair.att === null" class="artifacts-task__meta">No ATT attributes</span>
              <span *ngIf="pair.error !== null" class="artifacts-task__error">{{ pair.error }}</span>
            </li>
          </ul>
        </ng-container>
        <div class="artifacts-form-actions">
          <button type="submit" class="artifacts-button artifacts-button--primary" [disabled]="!canUploadRvmAtt">
            {{ uploadInProgress ? 'Processing...' : 'Upload unqueued RVM models' }}
          </button>
        </div>
      </form>

      <footer class="artifacts-dialog__footer">
        <span class="artifacts-hint">
          Uploads continue if you close this dialog. Track them on the
          <button
            type="button"
            class="artifacts-link-button"
            (click)="closeArtifactsImport(); setArtifactsTab('processings')"
          >
            Processings
          </button>
          tab.
        </span>
      </footer>
    </section>
  </div>

  <!-- Scene and model action dialog -->
  <div
    *ngIf="sceneModelAction !== null"
    class="artifacts-dialog-backdrop"
    (click)="!sceneModelActionPending && closeSceneModelAction()"
  >
    <form
      class="artifacts-dialog"
      [class.artifacts-dialog--wide]="sceneModelAction === 'instance'"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sceneModelActionTitle"
      (click)="$event.stopPropagation()"
      (submit)="$event.preventDefault(); confirmSceneModelAction()"
    >
      <header class="artifacts-dialog__header">
        <h2 id="sceneModelActionTitle">
          {{
            sceneModelAction === 'create'
              ? 'Create scene'
              : sceneModelAction === 'instance'
              ? 'Add model instances'
              : 'Attach models to scene'
          }}
        </h2>
        <button
          type="button"
          class="artifacts-icon-button"
          aria-label="Close"
          [disabled]="sceneModelActionPending"
          (click)="closeSceneModelAction()"
        >
          &#x2715;
        </button>
      </header>

      <div *ngIf="ingestState.error !== null" class="artifacts-error" role="alert">{{ ingestState.error }}</div>

      <ng-container *ngIf="sceneModelAction === 'create'">
        <div class="artifacts-field">
          <label class="artifacts-field__label" for="sceneActionModel">Model</label>
          <artifact-picker
            inputId="sceneActionModel"
            placeholder="Select model"
            searchPlaceholder="Search models by name, id or date"
            ariaLabel="Model"
            [options]="modelPickerOptions"
            [value]="sceneActionModelId"
            [disabled]="sceneModelActionPending"
            (valueChange)="setSceneActionModel($event)"
          ></artifact-picker>
        </div>
        <label class="artifacts-field">
          <span class="artifacts-field__label">Scene name</span>
          <input
            id="sceneActionName"
            type="text"
            [value]="sceneActionNameDraft"
            [disabled]="sceneModelActionPending"
            (input)="onSceneActionNameDraftChanged($event)"
            placeholder="Scene name"
          />
        </label>
      </ng-container>

      <div *ngIf="sceneModelAction === 'attach' || sceneModelAction === 'instance'" class="artifacts-field">
        <label class="artifacts-field__label" for="sceneActionScene">Target scene</label>
        <artifact-picker
          inputId="sceneActionScene"
          placeholder="Select scene"
          searchPlaceholder="Search scenes by name, id or date"
          ariaLabel="Target scene"
          [options]="scenePickerOptions"
          [value]="sceneActionSceneId"
          [disabled]="sceneModelActionPending"
          (valueChange)="setSceneActionScene($event)"
        ></artifact-picker>
      </div>

      <fieldset *ngIf="sceneModelAction === 'attach'" class="artifacts-checklist" aria-label="Models to attach">
        <legend class="artifacts-field__label">
          Models to attach
          <span *ngIf="sceneActionModelIds.length > 0">({{ sceneActionModelIds.length }} selected)</span>
        </legend>
        <input
          type="search"
          class="artifacts-checklist__search"
          placeholder="Filter models by name, id or date"
          aria-label="Filter models to attach"
          [value]="sceneActionModelFilter"
          (input)="onSceneActionModelFilterChanged($event)"
        />
        <label *ngFor="let model of filteredSceneActionModels" class="artifacts-checklist__item">
          <input
            type="checkbox"
            [checked]="sceneActionModelIds.includes(model.id)"
            [disabled]="sceneModelActionPending"
            (change)="onSceneActionAttachModelChanged(model.id, $event)"
          />
          <span class="artifacts-row__title">{{ getModelTitle(model) }}</span>
          <span class="artifacts-row__meta">
            <code>{{ model.id.slice(0, 6) }}</code> <time>{{ getModelDisplayDate(model) }}</time>
          </span>
        </label>
        <div *ngIf="modelsRegistry.length === 0" class="artifacts-hint">No models available.</div>
        <div
          *ngIf="modelsRegistry.length > 0 && filteredSceneActionModels.length === 0"
          class="artifacts-hint artifacts-checklist__empty"
        >
          No models match "{{ sceneActionModelFilter.trim() }}".
        </div>
      </fieldset>

      <ng-container *ngIf="sceneModelAction === 'instance'">
        <div *ngFor="let draft of sceneInstanceDrafts; let instanceIndex = index" class="artifacts-instance-draft">
          <div class="artifacts-instance-draft__header">
            <strong>Instance {{ instanceIndex + 1 }}</strong>
            <button
              type="button"
              class="artifacts-link-button"
              [disabled]="sceneModelActionPending || sceneInstanceDrafts.length === 1"
              (click)="removeSceneModelInstanceDraft(instanceIndex)"
            >
              Remove
            </button>
          </div>
          <div class="artifacts-instance-draft__identity">
            <div class="artifacts-field">
              <label class="artifacts-field__label" [for]="'sceneInstanceModel-' + instanceIndex">Model</label>
              <artifact-picker
                placeholder="Select model"
                searchPlaceholder="Search models by name, id or date"
                [inputId]="'sceneInstanceModel-' + instanceIndex"
                [ariaLabel]="'Model for instance ' + (instanceIndex + 1)"
                [options]="modelPickerOptions"
                [value]="draft.modelId"
                [disabled]="sceneModelActionPending"
                (valueChange)="setSceneInstanceModel(instanceIndex, $event)"
              ></artifact-picker>
            </div>
            <label class="artifacts-field">
              <span class="artifacts-field__label">Name</span>
              <input
                [id]="'sceneInstanceName-' + instanceIndex"
                type="text"
                [value]="draft.instanceName"
                [disabled]="sceneModelActionPending"
                (input)="onSceneInstanceNameChanged(instanceIndex, $event)"
                placeholder="Instance name"
              />
            </label>
          </div>
          <div class="artifacts-instance-draft__transforms">
            <fieldset
              *ngFor="
                let group of [
                  { key: 'translation', label: 'Translation' },
                  { key: 'rotation', label: 'Rotation (degrees)' },
                  { key: 'scale', label: 'Scale' }
                ]
              "
              class="artifacts-axes"
            >
              <legend>{{ group.label }}</legend>
              <label *ngFor="let axis of ['x', 'y', 'z']" class="artifacts-axes__axis">
                <span>{{ axis | uppercase }}</span>
                <input
                  type="number"
                  step="any"
                  [value]="$any(draft)[group.key][axis]"
                  [disabled]="sceneModelActionPending"
                  [attr.aria-label]="group.label + ' ' + (axis | uppercase)"
                  (input)="onSceneInstanceTransformChanged(instanceIndex, $any(group.key), $any(axis), $event)"
                />
              </label>
            </fieldset>
          </div>
        </div>
        <button
          type="button"
          class="artifacts-button"
          [disabled]="sceneModelActionPending"
          (click)="addSceneModelInstanceDraft()"
        >
          + Add another instance
        </button>
      </ng-container>

      <footer class="artifacts-dialog__footer artifacts-form-actions">
        <button
          type="button"
          class="artifacts-button"
          [disabled]="sceneModelActionPending"
          (click)="closeSceneModelAction()"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="artifacts-button artifacts-button--primary"
          [disabled]="
            sceneModelActionPending ||
            (sceneModelAction === 'create' && sceneActionModelId === null) ||
            (sceneModelAction === 'attach' && sceneActionModelIds.length === 0) ||
            ((sceneModelAction === 'attach' || sceneModelAction === 'instance') && sceneActionSceneId === null) ||
            (sceneModelAction === 'instance' && hasInvalidSceneModelInstanceDrafts())
          "
        >
          {{
            sceneModelActionPending
              ? 'Saving...'
              : sceneModelAction === 'create'
              ? 'Create scene'
              : sceneModelAction === 'instance'
              ? 'Add ' + sceneInstanceDrafts.length + (sceneInstanceDrafts.length === 1 ? ' instance' : ' instances')
              : sceneActionModelIds.length === 0
              ? 'Attach models'
              : 'Attach ' +
                sceneActionModelIds.length +
                (sceneActionModelIds.length === 1 ? ' model' : ' models')
          }}
        </button>
      </footer>
    </form>
  </div>

  <!-- Confirmation dialog -->
  <div *ngIf="artifactsConfirm as confirmation" class="artifacts-dialog-backdrop" (click)="cancelArtifactsConfirm()">
    <section
      class="artifacts-dialog artifacts-dialog--narrow"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="artifactsConfirmTitle"
      aria-describedby="artifactsConfirmMessage"
      (click)="$event.stopPropagation()"
    >
      <h2 id="artifactsConfirmTitle">{{ confirmation.title }}</h2>
      <p id="artifactsConfirmMessage">{{ confirmation.message }}</p>
      <footer class="artifacts-dialog__footer artifacts-form-actions">
        <button
          type="button"
          class="artifacts-button"
          [disabled]="artifactsConfirmPending"
          (click)="cancelArtifactsConfirm()"
        >
          Cancel
        </button>
        <button
          type="button"
          class="artifacts-button artifacts-button--danger-solid"
          [disabled]="artifactsConfirmPending"
          (click)="confirmArtifactsAction()"
        >
          {{ artifactsConfirmPending ? 'Working...' : confirmation.confirmLabel }}
        </button>
      </footer>
    </section>
  </div>
</main>
